import { LayerIdEnum } from '@/utils/constant';
import type { FeatureLike, LayerLike } from './types';

export class LayerRegistry {
  private registry = new Map<string, LayerLike>();

  polygons: FeatureLike[] = [];
  points: FeatureLike[] = [];
  lines: FeatureLike[] = [];

  attributeMarkLayerVisible = true;
  curZoom = 18;

  constructor(private map: any) {}

  ensureLayer = (layerId: string) => {
    const existed = this.registry.get(layerId);
    if (existed) {
      return existed;
    }
    return this.createLayer(layerId);
  };

  registerFeature = (feature: FeatureLike) => {
    if (feature.geometryType === 'Polygon') {
      this.polygons = this.polygons.filter(
        (polygon) => polygon.getId() !== feature.getId()
      );
      this.polygons.push(feature);
    }
    if (feature.geometryType === 'Point') {
      this.points = this.points.filter(
        (point) => point.getId() !== feature.getId()
      );
      this.points.push(feature);
    }
    if (feature.geometryType === 'LineString') {
      this.lines = this.lines.filter(
        (line) => line.getId() !== feature.getId()
      );
      this.lines.push(feature);
    }
    return feature;
  };

  unregisterFeature = (
    featureId: string | number,
    pointFeatureId?: string | number
  ) => {
    this.polygons = this.polygons.filter(
      (polygon) => polygon.getId() !== featureId
    );
    this.points = this.points.filter(
      (point) => point.getId() !== pointFeatureId
    );
  };

  clearDrawnFeatures = () => {
    this.ensureLayer(LayerIdEnum.ANCHOR_LAYER).clear();
    this.polygons = [];
    this.lines = [];
    this.points = [];
    this.getLayer(LayerIdEnum.ATTRIBUTE_MARK_LAYER)?.getSource().clear();
  };

  createLayer = (layerId: string) => {
    const features = new Map<string | number, FeatureLike>();
    let visible = true;

    const addFeatureOverlays = (feature: FeatureLike) => {
      this.map.addOverlay(feature.overlay);
      feature.auxiliaryOverlays?.forEach((overlay) => {
        this.map.addOverlay(overlay);
      });
    };

    const removeFeatureOverlays = (feature: FeatureLike) => {
      this.map.removeOverlay(feature.overlay);
      feature.auxiliaryOverlays?.forEach((overlay) => {
        this.map.removeOverlay(overlay);
      });
    };

    const setFeatureVisible = (feature: FeatureLike, nextVisible: boolean) => {
      const overlays = [feature.overlay, ...(feature.auxiliaryOverlays ?? [])];
      overlays.forEach((overlay) => {
        if (nextVisible) {
          overlay.show?.();
        } else {
          overlay.hide?.();
        }
      });
    };
    const layer: LayerLike = {
      layerId,
      source: {
        addFeature: (feature: FeatureLike) => {
          const existingFeature = features.get(feature.getId());
          if (existingFeature) {
            removeFeatureOverlays(existingFeature);
          }
          features.set(feature.getId(), feature);
          addFeatureOverlays(feature);
          if (!visible) {
            setFeatureVisible(feature, false);
          }
        },
        clear: () => {
          features.forEach((feature) => {
            removeFeatureOverlays(feature);
          });
          features.clear();
        },
        getFeatureById: (id: string | number) => features.get(id) ?? null,
      },
      addFeature: (feature: FeatureLike) => {
        layer.source.addFeature(feature);
      },
      removeFeature: (featureId: string | number) => {
        const feature = features.get(featureId);
        if (!feature) {
          return;
        }
        removeFeatureOverlays(feature);
        features.delete(featureId);
      },
      clear: () => layer.source.clear(),
      setVisible: (nextVisible: boolean) => {
        visible = nextVisible;
        features.forEach((feature) => {
          setFeatureVisible(feature, nextVisible);
        });
      },
      getSource: () => layer.source,
    };
    this.registry.set(layerId, layer);
    return layer;
  };

  getLayer = (layerId: string | undefined | null) => {
    if (!layerId) {
      return null;
    }
    return this.registry.get(layerId) ?? null;
  };

  changeLayerVisible = (layerId: any, visible: boolean) => {
    this.getLayer(layerId)?.setVisible(visible);
    if (layerId === LayerIdEnum.ANCHOR_LAYER) {
      this.attributeMarkLayerVisible = visible;
      if (visible === true && this.curZoom < 17) {
        return;
      }
      this.getLayer(LayerIdEnum.ATTRIBUTE_MARK_LAYER)?.setVisible(visible);
    }
  };

  clearLayer(layerId: string) {
    const layer = this.getLayer(layerId);
    layer && layer.clear();
    this.registry.delete(layerId);
  }

  addLayers = (layers: { layer: any; layerId: string }[]) => {
    layers?.forEach(({ layer, layerId }) => {
      this.registry.set(layerId, layer);
      if (layer?.addToMap) {
        layer.addToMap(this.map);
      }
    });
  };

  /** 批量添加要素到指定图层 */
  addFeaturesBatch = (layerId: string, features: FeatureLike[]) => {
    const layer = this.ensureLayer(layerId);
    features.forEach((feature) => {
      this.registerFeature(feature);
      layer.source.addFeature(feature);
    });
  };

  clearAll = () => {
    this.registry.forEach((layer) => layer.clear());
    this.registry.clear();
  };

  handleZoomChange = (zoom: number) => {
    this.curZoom = zoom;
    if (!this.attributeMarkLayerVisible) {
      return;
    }
    this.changeLayerVisible(LayerIdEnum.ATTRIBUTE_MARK_LAYER, zoom >= 17);
  };
}
